<template>
  <div class="account-reset">
    <div class="context-warp">
      <el-scrollbar overflow-x="hidden" ref="scrollbar" wrap-class="reset-scrollbar-wrap">
        <el-form
          :model="resetPwForm"
          :rules="resetRules"
          class="reset-form"
          label-position="left"
          ref="resetPwForm"
          content-width="100%"
        >
          <el-form-item prop="email" ref="emailFormItem">
            <template #label>
              <i class="icomoon-common_user" />
            </template>
            <el-input
              :maxlength="128"
              :placeholder="$t('e-mail')"
              :tips="$t('v_email')"
              v-model="resetPwForm.email"
            ></el-input>
          </el-form-item>
          <el-form-item prop="code" ref="codeFormItem">
            <template #label>
              <i class="icomoon-common_lock" />
            </template>
            <el-input
              :maxlength="6"
              :placeholder="$t('enter_hc_code')"
              :tips="$t('v_code')"
              class="code-input"
              data-input-type="ten"
              ref="verifyInput"
              v-input-limit
              v-model.trim="resetPwForm.code"
            ></el-input>
            <el-button
              :disabled="!getCodeStatus"
              :max-width="135"
              @click="getVerificationCode"
              class="code-button"
              type="link"
              v-loading="btnLoading"
            >
              <span :title="$t(getCodeText)" class="code-text ellipsis">{{ $t(getCodeText) }}</span>
              <span v-if="codeTimeout !== 0">{{ '(' + codeTimeout + 's)' }}</span>
            </el-button>
          </el-form-item>
          <!--这个input的作用是解决浏览器自动填充的问题-->
          <el-input disabled style="position: fixed; bottom: -10000px"></el-input>
          <el-form-item prop="password" ref="passwordFormItem">
            <template #label>
              <i class="icomoon-common_lock" />
            </template>
            <div>
              <cloud-password
                :new-model="true"
                :tips-max-width="900"
                :placeholder="$t('input_entry_new_password')"
                :tips="$t('HPP-web_hint_password_strong')"
                auto-complete="new-password"
                v-model.trim="resetPwForm.password"
              ></cloud-password>
            </div>
          </el-form-item>
        </el-form>
      </el-scrollbar>
    </div>
    <div class="button-wrap">
      <el-button :title="$t('btn_check_email')" @click="resetPassword" class="entry-button" type="primary">
        {{ $t('btn_check_email') }}
      </el-button>
    </div>
  </div>
</template>
<script>
import getPasswordStrongStrength from '@pro/common/utils/getPasswordStrongStrength';
import CloudPassword from '@/Common/components/cloud-password';
import { handleFingerPrint } from '@/Common/libs/utils';
import cryptoRSA from '@pro/common/utils/cryptoRSA';
import cryptoAES from '@pro/common/utils/cryptoAES';

export default {
  name: 'AccountReset',
  components: {
    CloudPassword
  },
  props: {
    autoEmail: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      resetPwForm: {
        email: '', //电子邮件
        code: '', //验证码
        password: '' //密码
      },
      codeTimeout: 0, //验证码倒计时
      getCodeStatus: true,
      getCodeText: 'get_code',
      btnLoading: false
    };
  },
  computed: {
    resetRules() {
      return {
        email: [
          { type: 'string', required: true, message: this.$t('v_not_empty') },
          { type: 'string', required: true, pattern: /\S/, message: this.$t('v_not_only_include_space') },
          {
            type: 'string',
            required: true,
            pattern: /^\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/,
            message: this.$t('v_email_64')
          }
        ],
        code: [
          { type: 'string', required: true, message: this.$t('v_not_empty') },
          { type: 'string', required: true, pattern: /\S/, message: this.$t('v_not_only_include_space') }
        ],
        password: [
          { type: 'string', required: true, message: this.$t('v_not_empty') },
          { type: 'string', required: true, pattern: /\S/, message: this.$t('v_not_only_include_space') },
          { validator: this.validatePassword, trigger: 'blur' }
        ]
      };
    }
  },
  watch: {
    autoEmail(value) {
      if (value) {
        this.resetPwForm.email = value;
      }
    },
    'resetPwForm.email'() {
      this.$refs.emailFormItem.resetValidate();
    },
    'resetPwForm.code'() {
      this.$refs.codeFormItem.resetValidate();
    },
    'resetPwForm.password'() {
      this.$refs.passwordFormItem.resetValidate();
    }
  },
  methods: {
    resetPassword() {
      this.$refs.resetPwForm.validate(async (valid) => {
        if (valid) {
          const loading = this.$loading();
          // 重置密码时使用rsa公钥加密密码
          const [rsaData] = await this.$api.getRsaPublicKey();
          const cryptedPassword = await cryptoRSA.encrypt(this.resetPwForm.password, rsaData.publicKey);

          const params = {
            email: this.resetPwForm.email,
            password: cryptedPassword,
            verifyCode: this.resetPwForm.code,
            loginClient: 2 //登录类型 0-android 1-ios 2-web
          };

          const [data] = await this.$api.resetAccountPw(params).finally(() => loading.close());

          if (data) {
            this.$message.success(this.$t('reset_password_success'));
            this.login();
          }
        }
      });
    },
    async login() {
      // 使用aes加密密码
      const aesKey = await cryptoAES.generateKey();
      const aesIv = cryptoAES.generateIv();
      const cryptedPassword = await cryptoAES.encrypt(this.resetPwForm.password, aesKey, aesIv);
      // 使用rsa加密aes秘钥和向量
      const [rsaData] = await this.$api.getRsaPublicKey();
      const aesKeyBuffer = await window.crypto.subtle.exportKey('raw', aesKey);
      const cryptedAESKey = await cryptoRSA.encrypt(aesKeyBuffer, rsaData.publicKey);
      const cryptedAESIv = await cryptoRSA.encrypt(aesIv, rsaData.publicKey);

      const params = {
        email: this.resetPwForm.email,
        password: cryptedPassword,
        aesKey: cryptedAESKey,
        aesIV: cryptedAESIv,
        loginType: 2,
        passwordType: 0,
        language: this.$i18n.locale === 'in' ? 'id' : this.$i18n.locale
      };

      let startTime = new Date().getTime();
      if (!params.terminalCode) {
        params.terminalCode = await handleFingerPrint('generate');
      }
      const [data] = await this.$api.login(params);
      let endTime = new Date().getTime();
      this.$bury.loginTimeCounting(endTime - startTime, 1);
      if (data) {
        sessionStorage.setItem('notLogUserInfo', JSON.stringify(data));
        window.location.href = `${window.location.origin}?hpcAfterLogin=true`;
      }
    },
    validatePassword(rule, value, callback) {
      if (value === '') {
        return callback();
      }

      const rankEmail1 = getPasswordStrongStrength(this.resetPwForm.email, value.toString());
      let rank = rankEmail1;
      if (rank) {
        return callback(new Error(this.$t(`HPP-web_need_password_strong_${rank}`)));
      }

      return callback();
    },
    getVerificationCode() {
      this.$refs.resetPwForm.validateField('email', async (error) => {
        if (!error) {
          this.btnLoading = true;
          const [data, err, res] = await this.$api.sendResetPWMail(this.resetPwForm.email).finally(() => {
            this.btnLoading = false;
          });

          if (data) {
            //发送验证码成功
            this.$message.success(this.$t('send_code_success'));
            this.codeTimeout = data.retryTime || 60;
            this.getCodeStatus = false;
            this.getCodeText = 'send_again';
            this.$refs.verifyInput.inputSelect();

            let timer = setInterval(() => {
              this.codeTimeout--;
              if (0 === this.codeTimeout) {
                clearInterval(timer);
                this.getCodeStatus = true;
              }
            }, 1000);
          } else {
            if (err.errorCode === 'LAP004041') {
              const domain = err.data.domain;
              // window.location.href = domain + '/#/resetPassword?email=' + encodeURIComponent(this.resetPwForm.email);
              window.location.href = `${domain}/#/login?type=jump&dialogType=reset&resetEmail=${encodeURIComponent(
                this.resetPwForm.email
              )}`;
            } else {
              this.$handleErrorAndTip(err.errorCode, res);
            }
          }
        }
      });
    },
    resetForm() {
      this.$refs.resetPwForm.resetFields();
      this.resetPwForm.email = this.autoEmail;
    }
  }
};
</script>
<style lang="scss" scoped>
.account-reset {
  ::v-deep .el-form-item__required {
    display: none !important;
  }
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 55px;
  align-content: flex-start;
  ::v-deep .reset-scrollbar-wrap {
    max-height: 300px;
    @media screen and (max-width: 1440px) {
      height: 200px;
    }
  }
  .reset-form {
    .code-button {
      font-size: 14px;
      position: absolute;
      top: 0;
      right: 0;
    }
  }
  .button-wrap {
    flex-grow: 1;
    display: flex;
    align-items: center;
  }
  .context-warp {
    align-self: flex-start;
    width: 100%;
  }
}
</style>
